import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls } from "@/lib/constants";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2, ArrowRight } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-sm">
        {!token ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 text-center">
            <h2 className="text-xl font-extrabold text-slate-900 font-heading">Invalid link</h2>
            <p className="text-sm text-slate-500 mt-2">This reset link is missing its token. Please request a new one.</p>
            <Link to="/forgot-password" className="mt-5 inline-block text-sm font-medium text-emerald-600">Request a new link</Link>
          </div>
        ) : done ? (
          <div data-testid="reset-success" className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto"><CheckCircle2 size={30} /></div>
            <h2 className="text-xl font-extrabold text-slate-900 font-heading mt-4">Password updated</h2>
            <p className="text-sm text-slate-500 mt-2">You can now sign in with your new password.</p>
            <button data-testid="reset-goto-login" onClick={() => navigate("/login")} className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm">Go to sign in <ArrowRight size={16} /></button>
          </div>
        ) : (
          <>
            <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4"><KeyRound size={22} /></div>
            <h2 className="text-2xl font-extrabold text-slate-900 font-heading">Set a new password</h2>
            <p className="text-sm text-slate-500 mt-1 mb-6">Choose a strong password for your account.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">New Password</label>
                <input data-testid="reset-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">Confirm Password</label>
                <input data-testid="reset-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} placeholder="••••••••" />
              </div>
              <button data-testid="reset-submit-button" type="submit" disabled={loading} className="w-full px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm transition-all shadow-sm active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? <Loader2 size={18} className="animate-spin" /> : "Update password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
